import { MapBrowserEvent } from 'ol';
import { Select, Translate } from 'ol/interaction';
import { ROUTE_WAY_POINT, ROUTE_LINE_STRING } from '../../utils/styles/styles';
import MultiFeature from './Multifeature';
import RouteFeature, { SELECTABLE_WAY_POINT, WAY_POINT_ROUTE_INDEX } from '../../utils/features/RouteFeature';
import LongPress, { LongPressEventType } from '../../utils/interactions/LongPress';
import LongPressAnim from '../../utils/overlay/LongPressAnim';
import { LIGHT_CONDITION } from '../../utils/constantes';
import {
  BLACK_COLOR,
  BLUE_COLOR,
  getGUIColor,
  GREEN_COLOR,
  WHITE_COLOR
} from '../../utils/GuiColor';
import AbstractCompositeLayer from './AbstractCompositeLayer';

const DURATION_BETWEEN_TWO_WAY_POINT_ADDITION = 400;

/**
 * @typedef {Object} RouteLayerOptionsAdditionnals
 * @property {import('../../style/styles').WayPointTextStyleCondition} wayPointStyle The way point style condition
 */

/**
 * @typedef {RouteLayerOptionsAdditionnals|import("./AbstractCompositeLayer").AbstractCompositeLayerOptions} RouteLayerOptions
 */

export default class RouteLayer extends AbstractCompositeLayer {
  /**
     * @constructor
     * @param {RouteLayerOptions} options The options.
     */
  constructor(options) {
    super(options);

    const { map } = options;

    /**
         * The long press animation
         */
    const longPressAnim = new LongPressAnim();

    /**
         * Because line feature can be selected multi times, this boolean indicates if it possible to add a new wap point
         */
    let canAddWayPoint = true;

    /** @type {import('../../style/styles').WayPointTextStyleCondition} */
    this.wayPointStyle_ = options.wayPointStyle || ((feature) => ({
      font: 'bold 16px \'Open Sans\'',
      offsetY: '1',
      text: `${feature.get(WAY_POINT_ROUTE_INDEX)}`,
    }));

    /**
         * Indicates if a feature is selected with down event.
         * @type {import('ol').Feature}
         */
    this.selectedFeature_ = null;

    /**
         * The route display in the layer.
         * @type {RouteFeature}
         */
    this.route_ = options.route || null;

    /**
         * Add the layer where route line is drawn. This layer must be added before way point layer to select way point before line.
         */
    this.layerLine_ = new MultiFeature();
    this.addLayer_(this.layerLine_);

    /**
         * Add the layer where way points are added. This layer must be added after line layer to select way point before line.
         * @private
         * @type {MultiFeature}
         */
    this.layer_ = new MultiFeature();
    this.addLayer_(this.layer_);

    /**
         * This method catch single click event before every other single click listener. It stops the event propagation.
         * @param {MapBrowserEvent<import('ol/MapBrowserEventType').default.SINGLECLICK>} event The map event.
         */
    this.catcher_ = (event) => {
      if (!this.selectedFeature_) {
        this.addWayPoint(event.coordinate);
      }
      event.stopPropagation();
    };

    /**
     * The longpress interaction to delete a waypoint
     */
    const longPressInteraction = new LongPress();
    longPressInteraction.on(LongPressEventType.LONGPRESS_START, () => {
      if (this.selectedFeature_ && this.selectedFeature_.getGeometry().getType() === 'Point') {
        map.addOverlay(longPressAnim);
        longPressAnim.start(this.selectedFeature_.getGeometry().getCoordinates());
      }
    })
    longPressInteraction.on(LongPressEventType.LONGPRESS_DONE, () => {
      this.removeWayPoint(this.selectedFeature_);
      map.removeOverlay(longPressAnim);
      longPressAnim.stop();
    });
    longPressInteraction.on(LongPressEventType.LONGPRESS_CANCEL, () => {
      map.removeOverlay(longPressAnim);
      longPressAnim.stop();
    });

    /**
     * The select way point interaction.
     * @private
     * @type {Select}
     */
    const selectInteraction = new Select({
      style: null,
      hitTolerance: 20,
      condition: (event) => event.type === 'pointerdown',
    });
    selectInteraction.on('select', (event) => {
      if (this.selectedFeature_) {
        this.selectedFeature_.setStyle();
      }
      this.selectedFeature_ = event.selected[0];

      if (this.selectedFeature_) {
        if (this.selectedFeature_.getGeometry().getType() === 'LineString' || !this.selectedFeature_.get(SELECTABLE_WAY_POINT)) {
          selectInteraction.getFeatures().clear();
          event.mapBrowserEvent.stopPropagation();
          if (canAddWayPoint) {
            // Timer to wait de time before to way point addition
            canAddWayPoint = false;
            setTimeout(() => canAddWayPoint = true, DURATION_BETWEEN_TWO_WAY_POINT_ADDITION);
            this.insertWayPoint(event.mapBrowserEvent.coordinate);
          }
          // Send event a second times to catch the down event with translate interactor.
          setTimeout(
            () => map.mapBrowserEventHandler_.dispatchEvent(
              new MapBrowserEvent(
                event.mapBrowserEvent.type,
                event.mapBrowserEvent.map,
                event.mapBrowserEvent.originalEvent,
                event.mapBrowserEvent.frameState,
              ),
            ),
            0,
          );
        } else {
          this.selectedFeature_.setStyle(this.selectedFeatureStyle_);
        }
      }
    });

    // Add the translate interaction.
    const translateInteraction = new Translate({
      layers: [this.layer_],
      features: selectInteraction.getFeatures(),
    });

    /*
         * Add interactions. Select is added after translate and longPress to be able to stop event before it send to translate.
         */
    this.addInteraction_(translateInteraction);
    this.addInteraction_(longPressInteraction);
    this.addInteraction_(selectInteraction);
    this.setActiveInteractions(false);

    const layerColor = localStorage.getItem('layerColor') || LIGHT_CONDITION.DAY
    this.setLightCondition(layerColor);
  }

  /**
     * Append way point to the route.
     * @param {import("ol/coordinate").Coordinate} coord The coordinate for the new point (projected).
     * @param {boolean} [selectable] Indicates if the wap point is selectable in the layer. Default is true.
     */
  addWayPoint(coord, selectable) {
    const point = this.route_.addWayPoint(coord, selectable);
    this.layer_.drawFeature(point);
  }

  /**
     * Insert the point in the closest segment of the route.
     * @param {import("ol/coordinate").Coordinate} coord The coordinate for the new point (projected).
     * @param {boolean} [selectable] Indicates if the wap point is selectable in the layer. Default is true.
     */
  insertWayPoint(coord) {
    const point = this.route_.insertWayPoint(coord);
    this.layer_.drawFeature(point);
  }

  /**
     * Remove a point from the route.
     * @param {WayPoint} wayPoint The way point of the route.
     */
  removeWayPoint(wayPoint) {
    this.route_.removeWayPoint(wayPoint);
    this.layer_.removeFeature(wayPoint);
  }

  /**
     * @return {Array<Ship>}
     */
  getFeatures() {
    return this.layer_.getFeatures();
  }

  /**
     * Activate/deactivate all managed interactions.
     * @param {boolean} activate True to activate all managed interaction, else false.
     */
  setActiveInteractions(activate) {
    if (activate) {
      this.route_ = new RouteFeature();
      this.layerLine_.drawFeature(this.route_);
      if (!this.getActiveInteractions()) {
        this.originalCatcher_ = this.map_.getCatchClickEvent();
      }
      this.map_.setCatchClickEvent(this.catcher_);
    } else if (this.originalCatcher_) {
      this.map_.setCatchClickEvent(this.originalCatcher_);
    }
    super.setActiveInteractions(activate);
    this.unselect_();
  }

  /**
     * Clear the elements in the layer.
     */
  clear() {
    // First interaction is select.
    // Remove the selected features.
    this.interactions_[2].getFeatures().clear();
    this.layer_.clear();
    this.layerLine_.clear();
    this.unselect_();
  }

  /**
     * Unselect the feature.
     */
  unselect_() {
    if (this.selectedFeature_) {
      this.selectedFeature_.setStyle();
      this.selectedFeature_ = null;
    }
  }

  /**
     * Set the light condition.
     * @param {import('../../constantes').LIGHT_CONDITION} lightCondition The light condition.
     */
  setLightCondition(lightCondition) {
    const black = getGUIColor(BLACK_COLOR, lightCondition);
    const white = getGUIColor(WHITE_COLOR, lightCondition);
    const green = getGUIColor(GREEN_COLOR, lightCondition);
    const blue = getGUIColor(BLUE_COLOR, lightCondition);
    this.layerLine_.setStyle(ROUTE_LINE_STRING(black));
    this.layer_.setStyle(ROUTE_WAY_POINT(this.wayPointStyle_, white, blue));
    this.selectedFeatureStyle_ = ROUTE_WAY_POINT(this.wayPointStyle_, white, green);
  }

  stop() {
    this.setActiveInteractions(false);
  }

  draw() {
    this.setActiveInteractions(true);
  }

  resumeDraw(myFeature) {
    this.route_ = myFeature
    this.layerLine_.drawFeature(this.route_)
    if (!this.getActiveInteractions()) this.originalCatcher_ = this.map_.getCatchClickEvent()
    this.map_.setCatchClickEvent(this.catcher_)
    if (this.originalCatcher_) this.map_.setCatchClickEvent(this.originalCatcher_)
    super.setActiveInteractions(true)
    this.unselect_()
  }

  resumDrawForWay() {
    if (!this.getActiveInteractions()) this.originalCatcher_ = this.map_.getCatchClickEvent()
    this.map_.setCatchClickEvent(this.catcher_)
    if (this.originalCatcher_) this.map_.setCatchClickEvent(this.originalCatcher_)
    super.setActiveInteractions(true)
    this.unselect_()
  }

  /**
     * Get the route feature in the layer.
     * @return {RouteFeature} The route feature.
     */
  getRoute() {
    return this.layerLine_.getFeatures()[0];
  }
}
